import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Truck, Package, Sparkles } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { customerApi } from "../lib/customerApi";
import { formatINR } from "../lib/api";

export default function OrderDetail() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const justPaid = params.get("paid") === "1";
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        customerApi.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setOrder(null)).finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-ivory">
                <Header />
                <div className="max-w-[1100px] mx-auto px-6 py-16 text-inkMuted">Loading…</div>
                <Footer />
            </div>
        );
    }
    if (!order) {
        return (
            <div className="min-h-screen bg-ivory">
                <Header />
                <div className="max-w-[1100px] mx-auto px-6 py-16">
                    <h1 className="font-display text-3xl">Order not found</h1>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory" data-testid="order-detail-page">
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12">
                {justPaid ? (
                    <section className="rounded-3xl bg-gradient-to-b from-skyblue/40 to-skyblue/10 border border-skyblueDeep/20 p-8 lg:p-10 mb-10" data-testid="order-success-banner">
                        <div className="flex items-center gap-3">
                            <span className="w-12 h-12 rounded-full bg-skyblueDeep text-white inline-flex items-center justify-center shadow-soft">
                                <CheckCircle2 size={22} />
                            </span>
                            <div>
                                <span className="label-eyebrow">Confirmed</span>
                                <h1 className="mt-1 font-display text-3xl sm:text-4xl text-inkDark tracking-tight">Order placed &mdash; thank you!</h1>
                            </div>
                        </div>
                        <p className="mt-4 text-inkDark/85 max-w-xl">Order <span className="font-mono">#{order.id.slice(0, 8)}</span> is confirmed. A confirmation will reach <strong>{order.shippingAddress.email}</strong> shortly.</p>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Pill icon={Sparkles} title="Atelier" body="Hand-finished by master artisans" />
                            <Pill icon={Package} title="Crafted" body="Quality-checked in our Mumbai atelier" />
                            <Pill icon={Truck} title="Ships in" body="2&ndash;3 business days" />
                        </div>
                    </section>
                ) : (
                    <>
                        <span className="label-eyebrow">Order #{order.id.slice(0, 8)}</span>
                        <h1 className="mt-3 font-display text-4xl text-inkDark tracking-tight">Order details</h1>
                    </>
                )}

                <div className="mt-2 text-inkMuted">
                    Placed {new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                    &nbsp;·&nbsp; Payment <strong className="text-inkDark capitalize">{order.paymentStatus}</strong>
                    &nbsp;·&nbsp; Fulfillment <strong className="text-inkDark capitalize">{order.fulfillmentStatus}</strong>
                    {order.razorpayPaymentId ? <>&nbsp;·&nbsp; Ref <span className="font-mono text-xs text-inkDark">{order.razorpayPaymentId}</span></> : null}
                </div>

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7 space-y-4" data-testid="order-items">
                        {order.items.map((it, idx) => (
                            <div key={idx} className="bg-ivory border border-inkDark/5 rounded-2xl p-4 flex gap-4">
                                <Link to={`/product/${it.slug}`} className="w-20 h-28 rounded-xl overflow-hidden bg-beige shrink-0">
                                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link to={`/product/${it.slug}`} className="font-serif text-lg text-inkDark hover:text-skyblueDeep">{it.name}</Link>
                                    <div className="label-eyebrow mt-1 text-[10px]">Qty {it.qty}{it.size ? ` · Size ${it.size}` : ""}</div>
                                    <div className="mt-1 text-sm text-inkDark">{formatINR(it.price)}</div>
                                </div>
                            </div>
                        ))}
                        <div className="pt-4 flex flex-wrap gap-3">
                            <Link to="/category/sarees" className="btn-pill text-sm" data-testid="order-continue-shopping">Continue shopping</Link>
                            <Link to="/orders" className="btn-ghost text-sm">All orders</Link>
                        </div>
                    </div>
                    <aside className="lg:col-span-5 space-y-5">
                        <div className="bg-beige rounded-2xl p-6">
                            <div className="font-display text-xl text-inkDark">Payment</div>
                            <dl className="mt-4 text-sm space-y-2">
                                <div className="flex justify-between"><dt className="text-inkMuted">Subtotal</dt><dd>{formatINR(order.subtotal)}</dd></div>
                                {order.discount > 0 ? (
                                    <div className="flex justify-between text-skyblueDeep"><dt>Discount {order.couponCode ? `(${order.couponCode})` : ""}</dt><dd>−{formatINR(order.discount)}</dd></div>
                                ) : null}
                                <div className="flex justify-between"><dt className="text-inkMuted">Shipping</dt><dd>Free</dd></div>
                                <div className="border-t border-inkDark/10 pt-2 flex justify-between font-medium text-base"><dt>Total</dt><dd>{formatINR(order.grandTotal)}</dd></div>
                            </dl>
                        </div>
                        <div className="bg-ivory border border-inkDark/5 rounded-2xl p-6">
                            <div className="font-display text-xl text-inkDark">Shipping address</div>
                            <address className="mt-3 not-italic text-sm text-inkDark space-y-1">
                                <div>{order.shippingAddress.name}</div>
                                <div className="text-inkMuted">{order.shippingAddress.phone} · {order.shippingAddress.email}</div>
                                <div>{order.shippingAddress.line1}</div>
                                {order.shippingAddress.line2 ? <div>{order.shippingAddress.line2}</div> : null}
                                <div>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</div>
                                <div>{order.shippingAddress.country}</div>
                            </address>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}

function Pill({ icon: Icon, title, body }) {
    return (
        <div className="bg-ivory rounded-xl p-3 flex items-center gap-3 shadow-soft">
            <Icon size={16} className="text-skyblueDeep" />
            <div className="text-xs">
                <div className="label-eyebrow text-[9px]">{title}</div>
                <div className="text-inkDark mt-0.5">{body}</div>
            </div>
        </div>
    );
}
